import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { inquiryService } from "@/features/inquiries/services/inquiryService";
import { companyService } from "@/features/company/services/companyService";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Button } from "@/shared/components/ui/Button";
import { Badge } from "@/shared/components/ui/Badge";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { formatDateTime } from "@/shared/utils/formatters";
import type { Inquiry, IssueOption, OfficerOption, CompanyOption } from "@/features/inquiries/types/inquiry";
import { InquiryDetailPanel } from "@/features/inquiries/components/InquiryDetailPanel";

const OFFICER_TYPES = ["officer", "admin"];

// Maps DB-stored abbreviations → display labels
const STATUS_DISPLAY_MAP: Record<string, string> = {
  "NEW": "New",
  "INPROG": "In Progress",
  "CLOSED": "Completed",
};

// Maps display labels → DB-stored abbreviations (for API filters)
const STATUS_DB_MAP: Record<string, string> = {
  "New": "NEW",
  "In Progress": "INPROG",
  "Completed": "CLOSED",
};

const statusVariant: Record<string, "default" | "success" | "warning" | "danger" | "info"> = {
  New: "info",
  NEW: "info",
  "In Progress": "warning",
  INPROG: "warning",
  Completed: "success",
  CLOSED: "success",
};

type SortKey = "ticket_id" | "title" | "status" | "company_name_text" | "batch_no" | "owner_name" | "type_name" | "assigned_to_name" | "last_update_dttm";
type SortDir = "asc" | "desc";

type ColKey = "ticket_id" | "title" | "status" | "company_name_text" | "batch_no" | "owner_name" | "type_name" | "assigned_to_name" | "priority" | "last_update_dttm";
const DEFAULT_COL_WIDTHS: Record<ColKey, number> = {
  ticket_id: 60,
  title: 200,
  status: 110,
  company_name_text: 130,
  batch_no: 90,
  owner_name: 120,
  type_name: 110,
  assigned_to_name: 130,
  priority: 70,
  last_update_dttm: 130,
};

const PAGE_SIZE = 50;

export const InquiryPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");

  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  // For users: default to New + In Progress; for officers: default to All
  const [statusFilter, setStatusFilter] = useState(isOfficer ? "All" : "Active");

  // Officer search fields
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchBatch, setSearchBatch] = useState("");
  const [searchOwner, setSearchOwner] = useState("");

  // Multi-select filter state
  const [companyFilter, setCompanyFilter] = useState<string[]>([]);
  const [companySearch, setCompanySearch] = useState("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const companyDropdownRef = useRef<HTMLDivElement>(null);

  const [assigneeFilter, setAssigneeFilter] = useState<string[]>([]);
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);

  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [typeSearch, setTypeSearch] = useState("");
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  // Officer dropdown data
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [officers, setOfficers] = useState<OfficerOption[]>([]);
  const [issueTypes, setIssueTypes] = useState<IssueOption[]>([]);

  // Detail panel
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Sorting
  const [sortKey, setSortKey] = useState<SortKey>("last_update_dttm");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Load officer dropdown data
  useEffect(() => {
    if (!isOfficer) return;
    const load = async () => {
      try {
        const [compData, issueData] = await Promise.all([
          companyService.getDropdown("ticketing"),
          inquiryService.getActiveIssues(i18n.language),
        ]);
        setCompanies(compData);
        setIssueTypes(issueData);
        if (userData?.businessUnit) {
          const offData = await inquiryService.getOfficersByBU(userData.businessUnit);
          setOfficers(offData);
        }
      } catch {
        // silently ignore dropdown errors
      }
    };
    load();
  }, [isOfficer, i18n.language, userData?.businessUnit]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) setCompanyDropdownOpen(false);
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(e.target as Node)) setAssigneeDropdownOpen(false);
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node)) setTypeDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const loadInquiries = useCallback(async () => {
    try {
      setLoading(true);
      const filters: Record<string, string> = {};

      // Status filter logic
      if (statusFilter === "Active") {
        // "Active" = New + In Progress, handled client-side by fetching all
        // We don't pass status so the API returns everything
      } else if (statusFilter !== "All") {
        filters.status = STATUS_DB_MAP[statusFilter] || statusFilter;
      }

      // Officer search parameters
      if (isOfficer) {
        if (searchBatch) filters.batch_no = searchBatch;
        if (searchOwner) filters.owner_id = searchOwner;
      }

      let data = await inquiryService.searchInquiries(filters, i18n.language);

      // Client-side filter for "Active" (New + In Progress), handles both display and DB values
      if (statusFilter === "Active") {
        data = data.filter((inq: Inquiry) =>
          inq.status === "New" || inq.status === "NEW" ||
          inq.status === "In Progress" || inq.status === "INPROG"
        );
      }

      setInquiries(data);
    } catch (error) {
      console.error("Failed to load inquiries:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, i18n.language, isOfficer, searchBatch, searchOwner]);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  // Auto-open panel when navigated from a notification
  useEffect(() => {
    const openTicketId = (location.state as { openTicketId?: string } | null)?.openTicketId;
    if (!openTicketId) return;
    inquiryService
      .searchInquiries({ ticket_id: openTicketId }, i18n.language)
      .then((data) => {
        const found = Array.isArray(data)
          ? data.find((inq) => String(inq.ticket_id) === String(openTicketId))
          : data;
        if (found) { setSelectedInquiry(found); setDetailOpen(true); }
      })
      .catch(console.error);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sorting logic
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "last_update_dttm" ? "desc" : "asc");
    }
  };

  const sortedInquiries = useMemo(() => {
    const sorted = [...inquiries].sort((a, b) => {
      let aVal: string | number | undefined;
      let bVal: string | number | undefined;

      switch (sortKey) {
        case "ticket_id":
          aVal = a.ticket_id;
          bVal = b.ticket_id;
          break;
        case "last_update_dttm":
          aVal = a.last_update_dttm || "";
          bVal = b.last_update_dttm || "";
          break;
        default:
          aVal = ((a as unknown as Record<string, unknown>)[sortKey] as string) || "";
          bVal = ((b as unknown as Record<string, unknown>)[sortKey] as string) || "";
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }

      const strA = String(aVal).toLowerCase();
      const strB = String(bVal).toLowerCase();
      if (strA < strB) return sortDir === "asc" ? -1 : 1;
      if (strA > strB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [inquiries, sortKey, sortDir]);

  const handleCompanyToggle = (id: string) => setCompanyFilter(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  const handleAssigneeToggle = (id: string) => setAssigneeFilter(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  const handleTypeToggle = (id: string) => setTypeFilter(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);

  const filteredInquiries = useMemo(() => {
    return sortedInquiries.filter(inq => {
      if (companyFilter.length > 0 && !companyFilter.includes(inq.company || "")) return false;
      if (assigneeFilter.length > 0 && !assigneeFilter.includes(inq.assigned_to || "")) return false;
      if (typeFilter.length > 0 && !typeFilter.includes(inq.type || "")) return false;
      return true;
    });
  }, [sortedInquiries, companyFilter, assigneeFilter, typeFilter]);

  useEffect(() => { setPage(0); }, [companyFilter, assigneeFilter, typeFilter, searchBatch, searchOwner, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredInquiries.length / PAGE_SIZE));
  const pagedInquiries = filteredInquiries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // CSV Export
  const exportCSV = () => {
    const headers = [
      t("inquiry.table.ticket_id"),
      t("inquiry.form.subject"),
      t("inquiry.status.title"),
      t("inquiry.table.company"),
      t("inquiry.table.batch"),
      t("inquiry.table.owner"),
      t("inquiry.form.assignee"),
      t("inquiry.table.type"),
      t("inquiry.footer.last_updated"),
    ];

    const escapeCSV = (val: string) => {
      if (val.includes(",") || val.includes('"') || val.includes("\n")) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const rows = filteredInquiries.map((inq) => [
      String(inq.ticket_id),
      escapeCSV(inq.title || ""),
      escapeCSV(STATUS_DISPLAY_MAP[inq.status || ""] || inq.status || ""),
      escapeCSV(inq.company_name_text || ""),
      escapeCSV(inq.batch_no || ""),
      escapeCSV(inq.owner_name || ""),
      escapeCSV(inq.assigned_to_name || inq.assigned_name || ""),
      escapeCSV(inq.type_name || ""),
      escapeCSV(inq.last_update_dttm ? formatDateTime(inq.last_update_dttm) : ""),
    ]);

    const csvContent = [headers.map(escapeCSV).join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `inquiries_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Clear officer search
  const clearSearch = () => {
    setCompanyFilter([]);
    setCompanySearch("");
    setAssigneeFilter([]);
    setAssigneeSearch("");
    setTypeFilter([]);
    setTypeSearch("");
    setSearchBatch("");
    setSearchOwner("");
  };

  // Sort indicator arrow
  const SortArrow = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <span className="ml-1 text-gray-300">&#8597;</span>;
    return <span className="ml-1">{sortDir === "asc" ? "▲" : "▼"}</span>;
  };

  // Column resizing
  const [colWidths, setColWidths] = useState<Record<ColKey, number>>(DEFAULT_COL_WIDTHS);
  const resizingRef = useRef<{ col: ColKey; startX: number; startWidth: number } | null>(null);

  const startResize = useCallback((e: React.MouseEvent, col: ColKey) => {
    e.stopPropagation();
    e.preventDefault();
    resizingRef.current = { col, startX: e.clientX, startWidth: colWidths[col] };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMouseMove = (me: MouseEvent) => {
      if (!resizingRef.current) return;
      const { col: resizingCol, startX, startWidth } = resizingRef.current;
      const diff = me.clientX - startX;
      const newWidth = Math.max(40, startWidth + diff);
      setColWidths(prev => ({ ...prev, [resizingCol]: newWidth }));
    };

    const onMouseUp = () => {
      resizingRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [colWidths]);

  const totalTableWidth = Object.values(colWidths).reduce((sum, w) => sum + w, 0);

  // Status tabs - users get "Active" instead of "All" as their combined view
  const statusTabs = isOfficer
    ? ["All", "New", "In Progress", "Completed"]
    : ["Active", "New", "In Progress", "Completed", "All"];

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "All": return t("inquiry.status.all");
      case "Active": return t("inquiry.status.active");
      case "New":
      case "NEW": return t("inquiry.status.new");
      case "In Progress":
      case "INPROG": return t("inquiry.status.inprogress");
      case "Completed":
      case "CLOSED": return t("inquiry.status.completed");
      default: return STATUS_DISPLAY_MAP[status] || status;
    }
  };

  if (loading && inquiries.length === 0) return <Spinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t("inquiry.title")}</h1>
        <div className="flex gap-2">
          {isOfficer && (
            <>
              <Button variant="secondary" size="sm" onClick={exportCSV} className="flex-1 md:flex-none">
                <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t("inquiry.table.export_csv")}
              </Button>
              <Button variant="secondary" onClick={() => navigate("/inquiries/audit")} className="flex-1 md:flex-none">
                {t("audit.title")}
              </Button>
            </>
          )}
          <Button onClick={() => navigate("/inquiries/new")} className="flex-1 md:flex-none">{t("inquiry.new_inquiry")}</Button>
        </div>
      </div>

      {/* Officer Search Panel */}
      {isOfficer && (
        <div className="mb-4">
          <button
            onClick={() => setSearchOpen((o) => !o)}
            className="flex items-center gap-2 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 active:text-indigo-700 transition-colors"
          >
            <svg className={`w-4 h-4 transition-transform ${searchOpen ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {t("inquiry.table.search_title")}
          </button>

          {searchOpen && (
            <div className="mt-3 p-4 bg-white rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Company */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("inquiry.table.company")}</label>
                  {companyFilter.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {companyFilter.map(val => {
                        const match = companies.find(c => c.value === val);
                        return (
                          <span key={val} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            {match?.label || val}
                            <button type="button" onClick={() => handleCompanyToggle(val)} className="hover:text-indigo-900">&times;</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div className="relative" ref={companyDropdownRef}>
                    <input
                      type="text"
                      value={companySearch}
                      onChange={e => { setCompanySearch(e.target.value); if (!companyDropdownOpen) setCompanyDropdownOpen(true); }}
                      onFocus={() => setCompanyDropdownOpen(true)}
                      placeholder={t("feed.form.search_company", "Search company...")}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {companyDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                        {companies.filter(c => c.label?.toLowerCase().includes(companySearch.toLowerCase())).map(c => {
                          const isSelected = companyFilter.includes(c.value);
                          return (
                            <button key={c.value} type="button" onClick={() => { handleCompanyToggle(c.value); setCompanySearch(""); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isSelected ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"}`}>
                              <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${isSelected ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"}`}>
                                {isSelected && "✓"}
                              </span>
                              {c.label}
                            </button>
                          );
                        })}
                        {companies.filter(c => c.label?.toLowerCase().includes(companySearch.toLowerCase())).length === 0 && (
                          <p className="px-3 py-2 text-xs text-gray-400 italic">{t("common.no_results", "No results found")}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Batch */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("inquiry.table.batch")}</label>
                  <input
                    type="text"
                    value={searchBatch}
                    onChange={(e) => setSearchBatch(e.target.value)}
                    placeholder={t("inquiry.table.batch_placeholder")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Created By (owner_id) */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("inquiry.table.owner")}</label>
                  <input
                    type="text"
                    value={searchOwner}
                    onChange={(e) => setSearchOwner(e.target.value)}
                    placeholder={t("inquiry.search_name")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Assigned To */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("inquiry.form.assignee")}</label>
                  {assigneeFilter.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {assigneeFilter.map(val => {
                        const match = officers.find(o => o.value === val);
                        return (
                          <span key={val} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            {match?.label || val}
                            <button type="button" onClick={() => handleAssigneeToggle(val)} className="hover:text-indigo-900">&times;</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div className="relative" ref={assigneeDropdownRef}>
                    <input
                      type="text"
                      value={assigneeSearch}
                      onChange={e => { setAssigneeSearch(e.target.value); if (!assigneeDropdownOpen) setAssigneeDropdownOpen(true); }}
                      onFocus={() => setAssigneeDropdownOpen(true)}
                      placeholder={t("inquiry.search_name", "Search assignee...")}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {assigneeDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                        {officers.filter(o => o.label?.toLowerCase().includes(assigneeSearch.toLowerCase())).map(o => {
                          const isSelected = assigneeFilter.includes(o.value);
                          return (
                            <button key={o.value} type="button" onClick={() => { handleAssigneeToggle(o.value); setAssigneeSearch(""); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isSelected ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"}`}>
                              <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${isSelected ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"}`}>
                                {isSelected && "✓"}
                              </span>
                              {o.label}
                            </button>
                          );
                        })}
                        {officers.filter(o => o.label?.toLowerCase().includes(assigneeSearch.toLowerCase())).length === 0 && (
                          <p className="px-3 py-2 text-xs text-gray-400 italic">{t("common.no_results", "No results found")}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Type */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("inquiry.table.type")}</label>
                  {typeFilter.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {typeFilter.map(val => {
                        const match = issueTypes.find(it => it.value === val);
                        return (
                          <span key={val} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            {match?.label || val}
                            <button type="button" onClick={() => handleTypeToggle(val)} className="hover:text-indigo-900">&times;</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div className="relative" ref={typeDropdownRef}>
                    <input
                      type="text"
                      value={typeSearch}
                      onChange={e => { setTypeSearch(e.target.value); if (!typeDropdownOpen) setTypeDropdownOpen(true); }}
                      onFocus={() => setTypeDropdownOpen(true)}
                      placeholder={t("common.select", "Select type...")}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {typeDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                        {issueTypes.filter(it => it.label?.toLowerCase().includes(typeSearch.toLowerCase())).map(it => {
                          const isSelected = typeFilter.includes(it.value);
                          return (
                            <button key={it.value} type="button" onClick={() => { handleTypeToggle(it.value); setTypeSearch(""); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isSelected ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"}`}>
                              <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${isSelected ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"}`}>
                                {isSelected && "✓"}
                              </span>
                              {it.label}
                            </button>
                          );
                        })}
                        {issueTypes.filter(it => it.label?.toLowerCase().includes(typeSearch.toLowerCase())).length === 0 && (
                          <p className="px-3 py-2 text-xs text-gray-400 italic">{t("common.no_results", "No results found")}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-3">
                <Button variant="ghost" size="sm" onClick={clearSearch}>
                  {t("common.clear_all")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status filter tabs */}
      <div className="flex gap-2 mb-4 md:mb-6 flex-wrap overflow-x-auto pb-1 -mx-3 px-3 md:mx-0 md:px-0">
        {statusTabs.map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-2 md:py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap active:scale-95 ${
              statusFilter === status ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {getStatusLabel(status)}
          </button>
        ))}
      </div>

      {loading ? (
        <Spinner />
      ) : filteredInquiries.length === 0 ? (
        <EmptyState title={t("inquiry.no_results")} />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-xs text-gray-500">
              {t("settings.search_showing", {
                from: page * PAGE_SIZE + 1,
                to: Math.min((page + 1) * PAGE_SIZE, filteredInquiries.length),
                total: filteredInquiries.length,
              })}
            </p>
          </div>
          <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
          <table className="text-sm" style={{ tableLayout: "fixed", width: totalTableWidth }}>
            <colgroup>
              {(Object.keys(DEFAULT_COL_WIDTHS) as ColKey[]).map(col => (
                <col key={col} style={{ width: colWidths[col] }} />
              ))}
            </colgroup>
            <thead className="bg-gray-50 text-left sticky top-0 z-10">
              <tr>
                {(
                  [
                    { col: "ticket_id" as ColKey, label: "#", sortable: true, sortKey: "ticket_id" as SortKey },
                    { col: "title" as ColKey, label: t("inquiry.form.subject"), sortable: true, sortKey: "title" as SortKey },
                    { col: "status" as ColKey, label: t("inquiry.status.title"), sortable: true, sortKey: "status" as SortKey },
                    { col: "company_name_text" as ColKey, label: t("inquiry.table.company"), sortable: true, sortKey: "company_name_text" as SortKey },
                    { col: "batch_no" as ColKey, label: t("inquiry.table.batch"), sortable: true, sortKey: "batch_no" as SortKey },
                    { col: "owner_name" as ColKey, label: t("inquiry.table.owner"), sortable: true, sortKey: "owner_name" as SortKey },
                    { col: "type_name" as ColKey, label: t("inquiry.table.type"), sortable: true, sortKey: "type_name" as SortKey },
                    { col: "assigned_to_name" as ColKey, label: t("inquiry.form.assignee"), sortable: true, sortKey: "assigned_to_name" as SortKey },
                    { col: "priority" as ColKey, label: t("inquiry.form.priority"), sortable: false, sortKey: null },
                    { col: "last_update_dttm" as ColKey, label: t("inquiry.footer.last_updated"), sortable: true, sortKey: "last_update_dttm" as SortKey },
                  ] as const
                ).map(({ col, label, sortable, sortKey: sk }) => (
                  <th
                    key={col}
                    className="px-4 py-3 font-medium text-gray-600 select-none whitespace-nowrap relative group overflow-hidden"
                    style={{ cursor: sortable ? "pointer" : "default" }}
                    onClick={sortable && sk ? () => handleSort(sk) : undefined}
                  >
                    <span className="truncate block pr-2">{label}{sortable && sk ? <SortArrow column={sk} /> : null}</span>
                    <div
                      className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize z-10 bg-transparent hover:bg-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      onMouseDown={(e) => startResize(e, col)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedInquiries.map((inq) => (
                <tr
                  key={inq.ticket_id}
                  onClick={() => { setSelectedInquiry(inq); setDetailOpen(true); }}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-3 text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap">{inq.ticket_id}</td>
                  <td className="px-4 py-3 font-medium text-gray-900 overflow-hidden">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate">{inq.title}</span>
                      <span className="flex items-center gap-1.5 text-xs text-gray-400 shrink-0">
                        {(inq.comment_count ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-0.5" title={t("inquiry.tabs.comments")}>
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                            {inq.comment_count}
                          </span>
                        )}
                        {(inq.attachments?.length ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-0.5" title={t("inquiry.tabs.attachments")}>
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                            {inq.attachments!.length}
                          </span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 overflow-hidden">
                    <Badge variant={statusVariant[inq.status || ""] || "default"}>
                      {getStatusLabel(inq.status || "")}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap">{inq.company_name_text || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap">{inq.batch_no || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap">{inq.owner_name || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap">{inq.type_name || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap">
                    {inq.assigned_to_name || inq.assigned_name || "-"}
                  </td>
                  <td className="px-4 py-3 overflow-hidden">
                    {inq.high_pri && (
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full" title={t("inquiry.form.priority")} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs overflow-hidden text-ellipsis">
                    {inq.last_update_dttm ? formatDateTime(inq.last_update_dttm) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                {t("settings.page_prev")}
              </Button>
              <span className="text-xs text-gray-500">{page + 1} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                {t("settings.page_next")}
              </Button>
            </div>
          )}
        </div>
      )}

      <InquiryDetailPanel
        inquiry={selectedInquiry}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onRefresh={loadInquiries}
      />
    </div>
  );
};
